import { DB_FILTER__ml_asset } from '@/orm/+model/ml_asset/DB_FILTER__ml_asset';
import { CONSTANTS_GENERATION__slider_step } from '@shared/constants/CONSTANTS_GENERATION__slider_step';
import { ENUM__ml_asset_type } from '@shared/dto/+model/sd/_enums/ENUM__ml_asset_type';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { DTO_GRAPH__instance } from '../../../_graph/DTO_GRAPH__instance';
import { ENUM__field_connectable_type } from '../../_base/_methods/ENUM__field_connectable_type';
import { ENUM__token_type } from '../../token/ENUM__token_type';
import { FIELD__token } from '../../token/FIELD__token';
import { DECORATOR__class_serialization_tested } from '../_base/DECORATOR__class_serialization_tested';
import { DECORATOR__class_serialization_tested_for_hash } from '../_base/DECORATOR__class_serialization_tested_for_hash';
import { DECORATOR__field_is_input } from '../_base/DECORATOR__field_is_input';
import { DECORATOR__field_not_linked } from '../_base/DECORATOR__field_not_linked';
import { FIELD_PRIMITIVE__int } from '../number/FIELD_PRIMITIVE__int';
import { FIELD__list_base_generic } from './FIELD__list_base_generic';

@DECORATOR__class_serialization_tested
@DECORATOR__class_serialization_tested_for_hash
export class FIELD__list_token extends FIELD__list_base_generic<FIELD__token> {
  /*
   * STATIC FIELDS
   */

  static _class_name_static = ENUM__dto_class_name.GRAPH_FIELD_LIST__TOKEN;

  /*
   *
   */

  /*
   * FIELDS META
   */
  token_weight_options: MAP__serializable<keyof FIELD_PRIMITIVE__int, any>;

  tokens_are_independent_to_each_other!: boolean;

  /*
   * FIELDS
   */

  sort_items(items: FIELD__token[]) {
    if (this.tokens_are_independent_to_each_other) {
      items.sort(FIELD__token.sort);
    }
  }

  /*
   * PROPERTIES
   */

  get items_visible() {
    return this.items.filter(item => !item.is_hidden.get_value());
  }

  get items_ready_to_delete() {
    return this.items.filter(item => !item.weight.meta.grid2d__is_dimension);
  }

  get items_non_ready_to_delete() {
    return this.items.filter(item => item.weight.meta.grid2d__is_dimension);
  }

  get_token_weight_slider_step() {
    return this.token_weight_options.get_or_assert('slider_step');
  }

  set_token_weight_slider_step(value: number) {
    this.token_weight_options.set('slider_step', value);

    for (let token of this.items) {
      token.weight.slider_step = value;
    }
  }

  get_token_weight_slider_step_values() {
    return this.token_weight_options.get_or_assert('slider_step_values');
  }

  get_index_to_insert_token_positive() {
    if (this.items.is_empty()) return 0;

    let out = this.items.findLastIndex(item => item.is_positive.get_value());

    if (out == -1) {
      out = this.items.length;
    } else {
      out = out + 1;
    }

    return out;
  }

  get_index_to_insert_token_negative() {
    if (this.items.is_empty()) return 0;

    let out = this.items.findLastIndex(item => !item.is_positive.get_value());

    if (out == -1) {
      out = this.items.length;
    } else {
      out = out + 1;
    }

    return out;
  }

  /*
   * METHODS
   */

  constructor() {
    super();

    this.tokens_are_independent_to_each_other = false;

    this.item_connectable_type = ENUM__field_connectable_type.TOKEN;

    this.token_weight_options = new MAP__serializable();

    this.token_weight_options.set('slider_step_values', CONSTANTS_GENERATION__slider_step.TOKEN_WEIGHT_VALUES);
    this.set_token_weight_slider_step(CONSTANTS_GENERATION__slider_step.TOKEN_WEIGHT_DEFAULT);
  }

  protected override _create_item_internal__default() {
    return new FIELD__token();
  }

  protected _init_item_new(item: FIELD__token) {
    super._init_item_new(item);

    item.weight.slider_step = this.get_token_weight_slider_step();
    item.weight.slider_step_values = this.get_token_weight_slider_step_values();
  }

  get_token_by_word_english(word_english: string) {
    let out = FIELD__token.CREATE_STUB();

    for (let token of this.items) {
      if (token.word.word_translated === word_english || token.word_english.get_value() === word_english) {
        out = token;
        break;
      }
    }

    return out;
  }

  get_tokens_by_words_english(words_english: string[]) {
    let out = [...this.items];

    out = out.filter(item => words_english.includes(item.word_english.get_value()));
    out.sort((a, b) => words_english.indexOf(a.word_english.get_value()) - words_english.indexOf(b.word_english.get_value()));

    return out;
  }

  get_tokens_by_words_english__except(words_english_exceptions: string[]) {
    let out = [...this.items];

    out = out.filter(item => !words_english_exceptions.includes(item.word_english.get_value()));

    return out;
  }

  get_enabled_and_non_zero_weight_items() {
    let out = [...this.items];

    out = out.filter(item => !item.is_hidden.get_value());
    out = out.filter(item => item.weight.get_value() != 0);

    return out;
  }

  @DECORATOR__field_not_linked()
  has_effect_on_generation_runtime(graph: DTO_GRAPH__instance) {
    let enabled_and_non_zero_weight_items = this.get_enabled_and_non_zero_weight_items();

    if (enabled_and_non_zero_weight_items.is_empty()) return false;

    return true;
  }

  get_db_filter_for_embedding_hashes(): DB_FILTER__ml_asset[] {
    let tokens_embeddings = this.items.filter(item => item.type == ENUM__token_type.EMBEDDING);

    let hashes = tokens_embeddings.map(item => item.word_english.get_value());

    let out = [];

    for (let hash_sha256 of hashes) {
      out.push({ type: ENUM__ml_asset_type.EMBEDDING, hash_sha256, family: this.meta.node_family });
    }

    return out;
  }

  @DECORATOR__field_not_linked()
  store__serialization_keys_list_white_for_hash(): string[] {
    let out: (keyof this)[] = super.store__serialization_keys_list_white_for_hash() as (keyof this)[];

    out.push('tokens_are_independent_to_each_other');

    return out as string[];
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  find_item_from_list(item_to_compare: FIELD__token) {
    return this.items.find_or_default(
      item => item.word_english.get_value() == item_to_compare.word_english.get_value() && item.type == item_to_compare.type,
      FIELD__token.CREATE_STUB(),
    );
  }

  /*
   *
   */
}
